// src/pages/Admin/CoursAgenda/CoursAgendaPage.jsx

import React, {
  useMemo,
  useState,
} from "react";

import {
  toast,
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";

import { useUser } from "../../Home/UserContext";

import { getRoleFromRoleId } from "../../../utils/roleHelper";

import useScrollToTop from "../../../hooks/useScollToTop";

import CoursAgendaHeader from "./components/CoursAgendaHeader";

import CoursAgendaFilters from "./components/CoursAgendaFilters";

import CoursAgendaWeekBoard from "./components/CoursAgendaWeekBoard";

import CoursAgendaDetailsDrawer from "./components/CoursAgendaDetailsDrawer";

import CoursAgendaFormDrawer from "./components/CoursAgendaFormDrawer";

import { useCoursAgendaAdmin } from "./hooks/useCoursAgendaAdmin";

import "./CoursAgendaStyles.css";

const CoursAgendaPage = () => {
  useScrollToTop();

  const { user } = useUser();

  const userRole = getRoleFromRoleId(
    user?.role_id
  );

  const {
    cours,
    filteredCours,

    enseignants,
    assistants,
    lieuxOptions,

    filters,
    setFilters,
    resetFilters,
    activeFiltersCount,

    loading,
    error,

    getCoursDetails,

    createCours,
    editCours,
    removeCours,
  } = useCoursAgendaAdmin();

  /* ------------------------------------------------------------------------ */
  /*                                  Drawers                                 */
  /* ------------------------------------------------------------------------ */

  const [
    detailsOpen,
    setDetailsOpen,
  ] = useState(false);

  const [
    formOpen,
    setFormOpen,
  ] = useState(false);

  const [
    selectedCours,
    setSelectedCours,
  ] = useState(null);

  const [formMode, setFormMode] =
    useState("create");

  const [saving, setSaving] =
    useState(false);

  const [deleting, setDeleting] =
    useState(false);

  /* ------------------------------------------------------------------------ */
  /*                                   Stats                                  */
  /* ------------------------------------------------------------------------ */

  const stats = useMemo(
    () => ({
      cours: cours.length,

      enseignants:
        enseignants.length,

      assistants:
        assistants.length,

      lieux: lieuxOptions.length,
    }),
    [
      cours.length,
      enseignants.length,
      assistants.length,
      lieuxOptions.length,
    ]
  );

  /* ------------------------------------------------------------------------ */
  /*                               Ouvrir détail                              */
  /* ------------------------------------------------------------------------ */

  const handleOpenDetails = async (
    coursId
  ) => {
    /*
     * Affichage immédiat à partir de la liste.
     */
    const localCours = cours.find(
      (item) =>
        Number(item.id) ===
        Number(coursId)
    );

    if (localCours) {
      setSelectedCours(localCours);
      setDetailsOpen(true);
    }

    /*
     * Puis récupération du détail complet.
     */
    try {
      const freshCours =
        await getCoursDetails(coursId);

      setSelectedCours(freshCours);
      setDetailsOpen(true);
    } catch (err) {
      console.error(
        "Erreur détail cours :",
        err
      );

      toast.error(
        "Impossible de charger le détail du cours."
      );
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Create                                  */
  /* ------------------------------------------------------------------------ */

  const handleCreate = () => {
    setSelectedCours(null);
    setFormMode("create");
    setFormOpen(true);
  };

  /* ------------------------------------------------------------------------ */
  /*                                   Edit                                   */
  /* ------------------------------------------------------------------------ */

  const handleEdit = (item) => {
    setDetailsOpen(false);

    setSelectedCours(item);

    setFormMode("edit");

    setFormOpen(true);
  };

  /* ------------------------------------------------------------------------ */
  /*                                Duplicate                                 */
  /* ------------------------------------------------------------------------ */

  const handleDuplicate = (item) => {
    setDetailsOpen(false);

    setSelectedCours(item);

    setFormMode("duplicate");

    setFormOpen(true);
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Submit                                  */
  /* ------------------------------------------------------------------------ */

  const handleFormSubmit = async (
    payload
  ) => {
    try {
      setSaving(true);

      if (
        formMode === "edit" &&
        selectedCours
      ) {
        await editCours(
          selectedCours.id,
          payload
        );

        toast.success(
          "Cours modifié avec succès."
        );
      } else {
        await createCours(payload);

        toast.success(
          formMode === "duplicate"
            ? "Copie du cours créée avec succès."
            : "Cours ajouté avec succès."
        );
      }

      setFormOpen(false);

      setSelectedCours(null);
    } catch (err) {
      console.error(
        "Erreur enregistrement cours :",
        err
      );

      toast.error(
        "Une erreur est survenue lors de l'enregistrement."
      );

      throw err;
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Delete                                  */
  /* ------------------------------------------------------------------------ */

  const handleDelete = async (
    item
  ) => {
    const confirmed =
      window.confirm(
        `Supprimer définitivement le cours "${item.nom_cours}" ?`
      );

    if (!confirmed) return;

    try {
      setDeleting(true);

      await removeCours(item.id);

      toast.success(
        "Cours supprimé avec succès."
      );

      setDetailsOpen(false);

      setSelectedCours(null);
    } catch (err) {
      console.error(
        "Erreur suppression cours :",
        err
      );

      toast.error(
        "Impossible de supprimer le cours."
      );
    } finally {
      setDeleting(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                  */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className="
        mx-auto
        mt-[130px]
        w-full
        max-w-[1800px]
        px-3
        pb-24
        text-slate-900
        dark:text-slate-100
        sm:px-5
        lg:px-7
      "
    >
      {/* Navigation globale */}
      <DashboardNavigation
        role={userRole}
      />

      <div className="mt-5 space-y-5">
        {/* Hero */}
        <CoursAgendaHeader
          totalCours={stats.cours}
          totalEnseignants={
            stats.enseignants
          }
          totalAssistants={
            stats.assistants
          }
          totalLieux={stats.lieux}
          onCreate={handleCreate}
        />

        {/* Filtres */}
        <CoursAgendaFilters
          filters={filters}
          setFilters={setFilters}
          lieuxOptions={
            lieuxOptions
          }
          enseignants={enseignants}
          assistants={assistants}
          resetFilters={
            resetFilters
          }
          activeFiltersCount={
            activeFiltersCount
          }
        />

        {/* Erreur */}
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm font-medium text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div
            className="
              flex min-h-[350px]
              items-center
              justify-center
              rounded-2xl
              border border-slate-200
              bg-white
              dark:border-slate-800
              dark:bg-slate-900
            "
          >
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-red-600 dark:border-slate-700 dark:border-t-red-400" />

              <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                Chargement de l'agenda...
              </p>
            </div>
          </div>
        ) : (
          <CoursAgendaWeekBoard
            cours={filteredCours}
            onOpen={
              handleOpenDetails
            }
          />
        )}
      </div>

      {/* Détail */}
      <CoursAgendaDetailsDrawer
        open={detailsOpen}
        cours={selectedCours}
        onClose={() =>
          setDetailsOpen(false)
        }
        onEdit={handleEdit}
        onDuplicate={
          handleDuplicate
        }
        onDelete={handleDelete}
        deleting={deleting}
      />

      {/* Create / Edit / Duplicate */}
      <CoursAgendaFormDrawer
        open={formOpen}
        mode={formMode}
        cours={selectedCours}
        enseignants={enseignants}
        assistants={assistants}
        saving={saving}
        onClose={() => {
          if (saving) return;

          setFormOpen(false);

          setSelectedCours(null);
        }}
        onSubmit={
          handleFormSubmit
        }
      />

      <ToastContainer />
    </div>
  );
};

export default CoursAgendaPage;